import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { certificates, Certificate, CertStatus } from '../../mocks/certificates';

/* ── Region color mapping ─────────────────────────────────────── */
const NORTHEAST_STATES = new Set(['CT','DE','MA','MD','ME','NH','NJ','NY','PA','RI','VT']);
const SOUTHEAST_STATES = new Set(['AL','AR','FL','GA','KY','LA','MS','NC','SC','TN','VA','WV']);
const MIDWEST_STATES   = new Set(['IA','IL','IN','KS','MI','MN','MO','ND','NE','OH','SD','WI']);
const SOUTHWEST_STATES = new Set(['AZ','CO','NM','NV','OK','TX','UT']);
// All others → WEST

const getStatePillClass = (state: string) => {
  if (NORTHEAST_STATES.has(state)) return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
  if (SOUTHEAST_STATES.has(state)) return 'bg-orange-500/15 text-orange-400 border-orange-500/20';
  if (MIDWEST_STATES.has(state))   return 'bg-lime-500/15 text-lime-400 border-lime-500/20';
  if (SOUTHWEST_STATES.has(state)) return 'bg-red-500/15 text-red-400 border-red-500/20';
  return 'bg-teal-500/15 text-teal-400 border-teal-500/20';
};

const STATUS_STYLE: Record<CertStatus, string> = {
  'Accepting Members': 'bg-green-500/15 text-green-400',
  'In Acquisition':   'bg-amber-500/15 text-amber-400',
  'Acquired':         'bg-white/10 text-white/40',
  'Closed':           'bg-red-500/10 text-red-400/50',
};
const STATUS_DOT: Record<CertStatus, string> = {
  'Accepting Members': 'bg-green-400',
  'In Acquisition':   'bg-amber-400',
  'Acquired':         'bg-white/30',
  'Closed':           'bg-red-400/50',
};

const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}K`
  : `$${n}`;

type SortKey = 'faceValue' | 'saleDate' | 'interestRate' | 'state';

/* All unique states in the mock */
const ALL_STATES = [...new Set(certificates.map((c) => c.state))].sort();

export default function CertificateListings() {
  const [stateFilter, setStateFilter] = useState('All');
  const [typeFilter, setTypeFilter]   = useState('All');
  const [statusFilter, setStatusFilter] = useState<CertStatus | 'All'>('All');
  const [sortBy, setSortBy]           = useState<SortKey>('saleDate');
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('asc');
  const [search, setSearch]           = useState('');

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let list = [...certificates];
    if (stateFilter  !== 'All') list = list.filter((c) => c.state   === stateFilter);
    if (typeFilter   !== 'All') list = list.filter((c) => c.type    === typeFilter);
    if (statusFilter !== 'All') list = list.filter((c) => c.status  === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        c.certNumber.toLowerCase().includes(q) ||
        c.county.toLowerCase().includes(q) ||
        c.propertyAddress.toLowerCase().includes(q) ||
        c.state.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let diff = 0;
      if (sortBy === 'faceValue')    diff = a.faceValue - b.faceValue;
      if (sortBy === 'interestRate') diff = (a.interestRate ?? 0) - (b.interestRate ?? 0);
      if (sortBy === 'state')        diff = a.state.localeCompare(b.state);
      if (sortBy === 'saleDate')     diff = a.saleDate.localeCompare(b.saleDate);
      return sortDir === 'asc' ? diff : -diff;
    });
    return list;
  }, [stateFilter, typeFilter, statusFilter, sortBy, sortDir, search]);

  const accepting = certificates.filter((c) => c.status === 'Accepting Members').length;
  const inAcq     = certificates.filter((c) => c.status === 'In Acquisition').length;

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortBy !== col) return <i className="ri-arrow-up-down-line ml-1 text-white/20" />;
    return sortDir === 'asc'
      ? <i className="ri-arrow-up-s-line ml-1 text-amber-400" />
      : <i className="ri-arrow-down-s-line ml-1 text-amber-400" />;
  };

  return (
    <div className="font-sans bg-[#090b10]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-16 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[500px] h-[400px] rounded-full bg-amber-500/4 blur-[120px]" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
            <span className="inline-block px-4 py-1.5 border border-amber-500/25 text-amber-400 text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Active Acquisition Opportunities — All 50 States
            </span>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white leading-tight">
                  Certificate Listings
                </h1>
                <p className="text-white/45 text-lg mt-4 max-w-xl">
                  Tax lien and foreclosure deed certificates our specialists are currently acquiring on behalf of members nationwide across all 50 US states.
                </p>
              </div>
              <div className="flex flex-col items-start md:items-end gap-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 text-sm font-semibold">{accepting} opportunities accepting members</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-amber-400/70 text-sm">{inAcq} currently in acquisition</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <div className="bg-[#0d1520] border-y border-white/5 sticky top-20 z-40">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
                <input
                  type="text"
                  placeholder="Search by state, county, address, or cert #…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white/4 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                {/* State dropdown */}
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="px-3 py-2 bg-white/4 border border-white/8 rounded-lg text-sm text-white/60 focus:outline-none focus:border-amber-500 cursor-pointer"
                >
                  <option value="All">All States</option>
                  {ALL_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {/* Type */}
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 bg-white/4 border border-white/8 rounded-lg text-sm text-white/60 focus:outline-none focus:border-amber-500 cursor-pointer">
                  <option value="All">All Types</option>
                  <option value="Tax Lien">Tax Lien</option>
                  <option value="Tax Deed">Tax Deed</option>
                </select>
                {/* Status */}
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as CertStatus | 'All')}
                  className="px-3 py-2 bg-white/4 border border-white/8 rounded-lg text-sm text-white/60 focus:outline-none focus:border-amber-500 cursor-pointer">
                  <option value="All">All Statuses</option>
                  <option value="Accepting Members">Accepting Members</option>
                  <option value="In Acquisition">In Acquisition</option>
                  <option value="Acquired">Acquired</option>
                </select>
                {(stateFilter !== 'All' || typeFilter !== 'All' || statusFilter !== 'All' || search) && (
                  <button
                    onClick={() => { setStateFilter('All'); setTypeFilter('All'); setStatusFilter('All'); setSearch(''); }}
                    className="px-3 py-2 text-xs text-white/40 hover:text-white/70 border border-white/8 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line mr-1" />Clear
                  </button>
                )}
              </div>
              <span className="text-white/25 text-xs whitespace-nowrap">{filtered.length} of {certificates.length} results</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <section className="py-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            {filtered.length === 0 ? (
              <div className="text-center py-24">
                <i className="ri-file-search-line text-white/10 text-6xl mb-4 block" />
                <p className="text-white/30">No certificates match your filters.</p>
                <button onClick={() => { setStateFilter('All'); setTypeFilter('All'); setStatusFilter('All'); setSearch(''); }}
                  className="mt-3 text-amber-400 text-sm font-semibold cursor-pointer hover:text-amber-300">
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="bg-[#0d1520] border border-white/6 rounded-2xl overflow-hidden">

                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto">
                  {/* Header */}
                  <div className="grid grid-cols-[80px_1fr_1fr_110px_110px_120px_150px] gap-4 px-6 py-3 border-b border-white/6 bg-white/2 min-w-[900px]">
                    <button onClick={() => toggleSort('state')} className="flex items-center text-white/25 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white/50 text-left">
                      State<SortIcon col="state" />
                    </button>
                    <p className="text-white/25 text-xs font-semibold uppercase tracking-wide">County</p>
                    <p className="text-white/25 text-xs font-semibold uppercase tracking-wide">Property Address</p>
                    <button onClick={() => toggleSort('faceValue')} className="flex items-center text-white/25 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white/50 text-left">
                      Face Value<SortIcon col="faceValue" />
                    </button>
                    <button onClick={() => toggleSort('interestRate')} className="flex items-center text-white/25 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white/50 text-left">
                      Interest Rate<SortIcon col="interestRate" />
                    </button>
                    <button onClick={() => toggleSort('saleDate')} className="flex items-center text-white/25 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:text-white/50 text-left">
                      Auction Date<SortIcon col="saleDate" />
                    </button>
                    <p className="text-white/25 text-xs font-semibold uppercase tracking-wide">Status</p>
                  </div>

                  {/* Rows */}
                  <div className="divide-y divide-white/4 min-w-[900px]">
                    {filtered.map((cert: Certificate) => (
                      <div key={cert.id} className="grid grid-cols-[80px_1fr_1fr_110px_110px_120px_150px] gap-4 px-6 py-4 hover:bg-white/2 transition-colors items-center">
                        {/* State */}
                        <div>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${getStatePillClass(cert.state)}`}>
                            {cert.state}
                          </span>
                        </div>
                        {/* County */}
                        <p className="text-white/55 text-sm truncate">{cert.county}</p>
                        {/* Property Address */}
                        <div>
                          <p className="text-white/75 text-xs leading-snug">{cert.propertyAddress}</p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md mt-1 inline-block whitespace-nowrap ${cert.type === 'Tax Lien' ? 'bg-amber-500/10 text-amber-400' : 'bg-white/8 text-white/40'}`}>
                            {cert.type}
                          </span>
                        </div>
                        {/* Face Value */}
                        <p className="text-white font-bold text-sm">{fmt(cert.faceValue)}</p>
                        {/* Interest Rate */}
                        <p className={`text-sm font-semibold ${cert.interestRate ? 'text-amber-400' : 'text-white/30'}`}>
                          {cert.interestRate ? `${cert.interestRate}% / yr` : '—'}
                        </p>
                        {/* Auction Date */}
                        <p className="text-white/45 text-xs">{cert.saleDate}</p>
                        {/* Status */}
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[cert.status]}`} />
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${STATUS_STYLE[cert.status]}`}>
                            {cert.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile card rows */}
                <div className="lg:hidden divide-y divide-white/4">
                  {filtered.map((cert: Certificate) => (
                    <div key={`m-${cert.id}`} className="p-5 flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded border flex-shrink-0 ${getStatePillClass(cert.state)}`}>{cert.state}</span>
                            <span className="text-white/50 text-xs truncate">{cert.county}</span>
                          </div>
                          <p className="text-white/65 text-xs leading-snug">{cert.propertyAddress}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[cert.status]}`} />
                          <span className={`text-xs font-semibold ${STATUS_STYLE[cert.status]} px-2 py-0.5 rounded-md whitespace-nowrap`}>{cert.status}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white/4 rounded-lg p-2">
                          <p className="text-white/30 text-xs">Face Value</p>
                          <p className="text-white font-bold text-sm mt-0.5">{fmt(cert.faceValue)}</p>
                        </div>
                        <div className="bg-white/4 rounded-lg p-2">
                          <p className="text-white/30 text-xs">Interest</p>
                          <p className="text-amber-400 font-bold text-sm mt-0.5">{cert.interestRate ? `${cert.interestRate}%` : '—'}</p>
                        </div>
                        <div className="bg-white/4 rounded-lg p-2">
                          <p className="text-white/30 text-xs">Auction Date</p>
                          <p className="text-white/60 text-xs mt-0.5">{cert.saleDate}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Members CTA */}
            <div className="mt-10 bg-[#0d1520] border border-amber-500/15 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6 justify-between">
              <div>
                <p className="text-xs text-amber-400 font-semibold uppercase tracking-widest mb-2">Member Exclusive</p>
                <h3 className="font-playfair text-2xl font-bold text-white mb-1">These Listings Are Acquired On Your Behalf</h3>
                <p className="text-white/40 text-sm max-w-lg">
                  Join a service plan to have our specialists acquire these certificates for your portfolio across all 50 states. Members receive detailed due-diligence reports and real-time acquisition status updates.
                </p>
              </div>
              <Link to="/pricing" className="flex-shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-amber-500 text-[#070c14] font-bold rounded-xl hover:bg-amber-400 transition-colors whitespace-nowrap">
                Join the Service <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
